<template>
    <div v-if="data">
        <div
            v-if="progressPercent >= 0"
            class="global-label label"
        >
            {{ $t("Checklist") }}
        </div>
        <div
            v-if="progressPercent >= 0"
            class="global-label text-right"
        >
            {{ progressPercent }}%
        </div>
        <div style="clear: both;"></div>
        <div
            v-if="progressPercent >= 0"
            class="progress"
        >
            <div
                class="done"
                :style="{ width: progressPercent + '%' }"
            ></div>
        </div>
        <draggable
            v-if="data"
            tag="ul"
            :list="data.items"
            draggable=".drag-item"
            ghost-class="ghost"
            class="pl-0"
            handle=".drag-item"
            :style="isFullPage ? 'margin-left: -10px;' : 'margin-left: -25px;'"
            @change="save"
            @start="() => (hoverControllerEnabled = false)"
            @end="() => (hoverControllerEnabled = true)"
        >
            <li
                v-for="(item, index) in data.items"
                :key="index"
                class="overflow-hidden"
                :class="['drag-item', { 'hovered': index === hoveredIndex }]"
                style="position: relative;"
                @mouseenter="controlHoverState(index, $event)"
                @mouseleave="controlHoverState(index, $event)"
            >
                <div
                    :class="[
                        'handle d-flex',
                        {
                            'hide': (!isFullPage && focusIndex > -1) || data.items.length < 2 || !item.description
                        }
                    ]"
                    style="cursor: move; align-items: center;"
                >
                    <icon
                        size="16"
                        icon="GripVertical"
                    />
                </div>
                <div class="cont align-start">
                    <div class="check-cont d-flex align-center">
                        <v-checkbox
                            v-model="item.checked"
                            color="grey"
                            hide-details
                            :ripple="false"
                            class="pt-0 mt-1"
                            @change="field_check(index)"
                        ></v-checkbox>
                    </div>
                    <div class="text-cont flex-grow-1">
                        <div>
                            <v-textarea
                                v-if="editIndex === index"
                                :id="'descriptionField_' + index"
                                ref="activeTextarea"
                                :value="getDescriptionWithoutLinks(item.description)"
                                dense
                                hide-details
                                auto-grow
                                rows="1"
                                row-height="24"
                                :class="['item-field font-weight-medium pa-0 mt-0 body-2', { 'checked': item.checked }]"
                                @blur="blur_field($event, index)"
                                @focus="focus_field(index)"
                                @keydown.stop.prevent.enter="save_and_next($event, index)"
                                @keydown.stop.prevent.tab.exact="save_and_next($event, index)"
                                @keydown.stop.shift.tab="save_and_prev($event, index)"
                                @paste="onPaste($event, index)"
                            ></v-textarea>
                            <p
                                v-else
                                style="word-break: break-word"
                                :class="['font-weight-medium', 'textarea-replacer', { 'checked': item.checked }]"
                                @click.prevent="onListItemClick($event, index)"
                                v-html="item.description"
                            ></p> <!-- INPUT CLEANED WHEN SAVING -->
                        </div>
                    </div>
                    <div
                        class="menu-cont"
                        :class="focusIndex === index ? 'd-none' : 'd-flex'"
                        style="position: absolute; right: 0; height: 100%;"
                    >
                        <action-menu
                            v-if="item.description"
                            icon="MoreHorizontal"
                            :items="actionItems"
                            @action="actionItems[$event].action(index)"
                        />
                        <v-tooltip v-if="item.description && canTrackTime" bottom>
                            <template v-slot:activator="{ on }">
                                <icon-button
                                    :on="on"
                                    icon-size="20"
                                    icon="PlayCircle"
                                    @click="startTimer(index)"
                                />
                            </template>
                            {{ $t("Start timer") }}
                        </v-tooltip>
                    </div>
                </div>
            </li>
        </draggable>
        <v-btn
            depressed
            class="grey-button no-bg font-italic"
            :style="!isFullPage ? 'margin-left: -12px;' : 'margin-left: -10px'"
            @click.stop="new_field()"
        >
            <icon :icon="data.items.length ? 'Plus' : 'Square'" />
            <span class="subtitle-2 font-weight-regular px-1">
                {{ data.items.length ? $t("Add an item") : $t("Add checklist item") }}
            </span>
        </v-btn>
    </div>
</template>

<script>
import errorDialog from "@/views/ErrorDialog/errorDialog"
import Vue from "vue";
import { ref, computed, watch } from "@vue/composition-api";
import AppModule from "../store/modules/app";
import TaskModule from "../store/modules/task";
import AccountModule from "../store/modules/account";
import draggable from "vuedraggable";
import {CustomFormats, formatDateCustom} from "../plugins/formatting";
import {$t} from "@/i18n";
import { getDescriptionWithLinks, getDescriptionWithoutLinks } from "@/helpers/formatting";

export default {
    name: "ChecklistField",
    components: { draggable },
    props: {
        taskId: {
            type: [String, Number],
            required: true
        },
        value: {
            type: Object,
            required: false,
            default: null
        },
        isTemporaryClientTask: {
            type: Boolean,
            default: false
        },
        isFullPage: {
            type: Boolean,
            default: false
        },
        autoSave: {
            type: [Boolean, Number],
            required: true
        },
        canTrackTime: {
            type: Boolean,
            default: false
        }
    },
    setup(props, ctx) {
        const appModule = AppModule();
        const { saveTaskField } = TaskModule();
        const { current_account } = AccountModule();

        const data = ref(Vue.clone_object(props.value));

        // We probably don't need both
        const editIndex = ref(-1);
        const focusIndex = ref(-1);

        const descriptionField = ref(null);
        const isSaveExecuting = ref(false);
        const changedRows = ref([]);
        const preventBlurSave = ref(false);
        const hoveredIndex = ref(null);
        const hoverControllerEnabled = ref(true);
        const activeTextarea = ref(null);

        let rollbackVal = null;
        let saved_state = props.value == null ? { items: []} : props.value;

        const value = computed(function() {
            return props.value;
        });

        watch(value, function(val) {
            if (editIndex.value >= 0) return;
            data.value = val ? Vue.clone_object(val) : { items: []};
        });

        const progressPercent = computed(function() {
            if (data.value == null) return -1;

            let total = data.value.items.length;
            let done = data.value.items.filter(x => x.checked).length;

            if (total === 0) return -1;

            return Math.round((done * 100) / total);
        });

        const has_changes = function() {
            if (data.value == null) return false;

            if (data.value.items.length !== saved_state.items.length) {
                return true;
            } else {
                for (let i = 0; i < data.value.items.length; i++) {
                    if (
                        data.value.items[i].checked !== saved_state.items[i].checked ||
                        data.value.items[i].description !== saved_state.items[i].description
                    ) {
                        return true;
                    }
                }
            }
            return false;
        };
        const save = function() {
            if (!has_changes()) return;
            if (props.taskId === 0 || props.isTemporaryClientTask || !props.autoSave) {
                ctx.emit("change", {
                    ...data.value,
                    items: data.value.items.filter(i => i.description.length && i.description.trim().length > 0)
                });
                return;
            }

            isSaveExecuting.value = true;

            //let form_data = Vue.clone_object(data.value);
            changedRows.value = [];

            saveTaskField({ id: props.taskId, checklist: data.value }, "ChecklistField.vue")
                .then(function() {
                    isSaveExecuting.value = false;

                    if (changedRows.value.length > 0) save();
                })
                .catch(function(ex) {
                    isSaveExecuting.value = false;
                    errorDialog.showError(ex);
                });
        };
        const push_save = function(idx) {
            changedRows.value.push(idx);

            if (!isSaveExecuting.value) {
                save();
            }
        };
        const edit_field = function(idx) {
            rollbackVal = data.value.items[idx].description;
            editIndex.value = idx;
            focusIndex.value = idx;

            Vue.nextTick(function() {
                // Make sure activeTextarea is mounted before focusing on it
                activeTextarea.value[0].focus();
            });
        };
        const new_field = function(desc, focus = true) {
            rollbackVal = "";
            const newItem = { "checked": false, "description": desc || "" };
            data.value.items.push(newItem);

            if (focus) {
                edit_field(data.value.items.length - 1);
            }
        };

        const updateDescription = function(val, idx) {
            const curChanges = data.value.items[idx].description

            data.value.items[idx].description = getDescriptionWithLinks(val)

            const hasChanges = curChanges !== data.value.items[idx].description

            if (hasChanges) {
                push_save(idx);
            }

            return hasChanges
        };

        const blur_field = function(e, idx) {
            if (preventBlurSave.value) {
                preventBlurSave.value = false;
                return;
            }
            updateDescription(e.target.value, idx);
            focusIndex.value = -1;
            editIndex.value = -1;
        };
        const save_and_next = function(e, idx) {
            let add = 1;
            preventBlurSave.value = true;
            updateDescription(e.target.value, idx);

            if (idx + add < data.value.items.length) edit_field(idx + add);
            else if (add == 1) new_field();

            preventBlurSave.value = false;
        };
        const save_and_prev = function(e, idx) {
            preventBlurSave.value = true;
            updateDescription(e.target.value, idx);

            if (idx > 0) edit_field(idx - 1);

            preventBlurSave.value = false;
        };

        const cancel_field = function(idx) {
            if (!rollbackVal || !data.value.items[idx].description) {
                data.value.items.splice(idx, 1);
                push_save(-1);
            } else {
                data.value.items[idx].description = rollbackVal;
                push_save(idx);
            }

            editIndex.value = -1;
        };
        const field_check = function(idx) {
            push_save(idx);
        };

        const focus_field = function(idx) {
            focusIndex.value = idx;
            editIndex.value = idx;
        };

        const initiateNewTask = function(idx) {
            data.value.items[idx].checked = true;
            data.value.items[idx].description = `[${current_account.value.name} ${formatDateCustom(
                new Date(Date.now()),
                CustomFormats.numeric_day_with_short_month
            )}] ${data.value.items[idx].description}`;

            field_check(idx);
            ctx.emit("convert-to-task", idx);
        };

        const deleteItem = function(idx) {
            cancel_field(idx);
        };

        const onPaste = function(e) {
            const pasted = e.clipboardData.getData("text");
            const pastedLines = pasted
                .trim()
                .split("\n")
                .map(i => i.trim())
                .filter(i => !["", "\n"].includes(i));

            if (pastedLines.length > 1) {
                e.preventDefault();
                pastedLines.map(line => {
                    new_field(line, false);
                });

                if (activeTextarea.value && activeTextarea.value[0]) {
                    activeTextarea.value[0].blur();
                }

                save()
            }
        };

        const onListItemClick = function(e, index) {
            if (e.target.tagName.toLowerCase() === "a") {
                window.open(e.target.href);
            } else {
                focusIndex.value = index;
                editIndex.value = index;
                Vue.nextTick(function() {
                    // Make sure activeTextarea is mounted before focusing on it
                    activeTextarea.value[0].focus();
                });
            }
        };

        const onItemDescriptionChange = function(input, index) {
            data.value.items[index].description = input;
        };

        if (data.value == null) {
            data.value = { items: []};
        }

        const controlHoverState = (idx, evt) => {
            /*
             *  Controlling the hover state via class instead of css pseudoclass because we need to cancel the hover state
             *  on demand - while the mouse is technically still over the element.
             */

            if (!hoverControllerEnabled.value) return;

            if (evt.type === "mouseleave") {
                hoveredIndex.value = null;
            } else if (evt.type === "mouseenter") {
                hoveredIndex.value = idx;
            }
        };

        const startTimer = (index) => {
            const description = data.value.items[index].description

            ctx.emit('start-timer', description)
        }

        const actionItems = computed(() => [
            {
                text: $t('Convert to a task'),
                action: initiateNewTask
            },
            {
                text: $t('Delete'),
                action: deleteItem
            }
        ])

        return {
            editIndex,
            data,
            save,
            descriptionField,
            progressPercent,
            edit_field,
            new_field,
            blur_field,
            cancel_field,
            save_and_next,
            save_and_prev,
            field_check,
            focus_field,
            focusIndex,
            controlHoverState,
            hoveredIndex,
            hoverControllerEnabled,
            initiateNewTask,
            deleteItem,
            onPaste,
            activeTextarea,
            onListItemClick,
            onItemDescriptionChange,
            actionItems,
            startTimer,
            getDescriptionWithLinks,
            getDescriptionWithoutLinks
        };
    }
};
</script>
<style lang="scss" scoped>
::v-deep .ghost {
    .cont {
        border-radius: 4px;
        background-color: var(--v-n1-base) !important;
        border: 1px dashed var(--v-n3-base) !important;

        & > div {
            visibility: hidden !important;
            opacity: 0;
        }
    }
}

ul {
    list-style-type: none;
}

::v-deep .v-input__slot .v-input--selection-controls__input {
    margin-right: 8px;
}

li {
    display: flex;

    &.hovered {
        .cont {
            background: var(--v-n1-base);
        }

        .handle:not(.hide) {
            visibility: visible;
            opacity: 1;
        }

        .menu-cont {
            visibility: visible;
            opacity: 1;
        }
    }
}

.handle {
    visibility: hidden;
    opacity: 0;
}

.cont {
    display: flex;
    flex-grow: 1;
    padding: 0 6px;
    border-radius: 4px;
}

.menu-cont {
    visibility: hidden;
    opacity: 0;

    &.show {
        visibility: visible;
        opacity: 1;
    }

    ::v-deep .v-btn {
        width: 40px;
        height: 32px;
        background: var(--v-n1-base);
        border: 1px solid var(--v-n2-base);
    }
}

.label {
    float: left;
    width: 50%;
}

.progress {
    height: 1px;
    background-color: var(--v-n2-base);
    margin-bottom: 8px;

    .done {
        height: 1px;
        background-color: var(--v-n4-base);
        transition: 0.2s map-get($transition, "fast-out-slow-in") width;
    }
}

::v-deep .item-field {
    &.checked {
        textarea {
            color: var(--v-g4-base);
        }
    }

    .v-input__slot:before {
        display: none;
    }

    textarea {
        padding-top: 4px;
        padding-bottom: 4px;
        padding-right: 44px;
        line-height: 24px;
    }
}

.textarea-replacer {
    margin: 0;
    letter-spacing: normal;
    min-height: 32px;

    padding-top: 4px;
    padding-bottom: 4px;
    line-height: 24px;

    &.checked {
        color: var(--v-g4-base);
    }
}
</style>
